import { FC } from 'react';
import { fmtMedida, nomeUf, siglaUf, type LinhaSkuVendas, type Medida, type SituacaoVenda, type TabelaSkuVendas } from '@/utils/equiplexVendas';
import { fmtPct, mesCurto } from '@/utils/painelFormat';
import { STATUS } from '../chartTheme';
import PainelCard from '../PainelCard';

interface Props {
  tabela: TabelaSkuVendas;
  medida: Medida;
  mesLabel: string;
  /** Janela da "principal UF": `últimos 12 meses` ou `no período`. */
  janelaUf?: string;
}

const SITUACAO: Record<SituacaoVenda, { rotulo: string; icone: string; cor: string } | null> = {
  normal: null,
  futuro: null,
  sem_venda: { rotulo: 'Sem venda', icone: 'block', cor: STATUS.serio },
  fora_do_forecast: { rotulo: 'Fora do forecast', icone: 'call_split', cor: STATUS.atencao },
};

const Situacao: FC<{ s: SituacaoVenda }> = ({ s }) => {
  const v = SITUACAO[s];
  if (!v) return null;
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-content-secondary">
      <span aria-hidden className="material-symbols-outlined text-[15px] leading-none" style={{ color: v.cor }}>
        {v.icone}
      </span>
      {v.rotulo}
    </span>
  );
};

const Linha: FC<{ l: LinhaSkuVendas; medida: Medida }> = ({ l, medida }) => (
  <tr className="border-b border-theme-border">
    <td className="py-1.5 pr-3 font-mono text-xs text-content-secondary">{l.codigoDatasul ?? l.codigo}</td>
    <td className="py-1.5 pr-3 text-content-primary">
      {l.descricao || '—'}
      <span className="ml-2 text-xs text-content-muted">{l.categoria}</span>
    </td>
    <td className="whitespace-nowrap py-1.5 pr-3 text-right tabular-nums">{fmtMedida(l.forecast, medida, false)}</td>
    <td className="whitespace-nowrap py-1.5 pr-3 text-right tabular-nums">{fmtMedida(l.faturado, medida, false)}</td>
    <td className="py-1.5 pr-3 text-right tabular-nums">{fmtPct(l.pctDoForecast)}</td>
    {l.proximos.map((v, i) => (
      <td key={i} className="whitespace-nowrap py-1.5 pr-3 text-right tabular-nums text-content-secondary">
        {fmtMedida(v, medida)}
      </td>
    ))}
    <td className="whitespace-nowrap py-1.5 pr-3 text-content-secondary" title={l.ufPrincipal ? nomeUf(l.ufPrincipal.uf) : undefined}>
      {l.ufPrincipal ? `${siglaUf(l.ufPrincipal.uf)} · ${fmtPct(l.ufPrincipal.share)}` : '—'}
    </td>
    <td className="py-1.5">
      <Situacao s={l.situacao} />
    </td>
  </tr>
);

/**
 * Forecast × faturado por SKU no mês, com o forecast dos próximos meses ao lado (o que o
 * Manus mostrava de ago a dez) e a UF que mais compra cada SKU no faturado real.
 */
const SkuVendasTable: FC<Props> = ({ tabela, medida, mesLabel, janelaUf = 'últimos 12 meses' }) => (
  <PainelCard
    testId="vendas-tabela-sku"
    titulo="Forecast × faturado por SKU"
    subtitulo={`${mesLabel}. Principal UF = quem mais comprou o SKU ${janelaUf === 'no período' ? 'no período' : `nos ${janelaUf}`}.`}
  >
    {tabela.linhas.length === 0 ? (
      <div className="py-10 text-center text-sm text-content-muted">Nem forecast nem venda neste mês.</div>
    ) : (
      <div className="relative overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-theme-border text-left text-xs uppercase tracking-wide text-content-muted">
              <th className="py-2 pr-3 font-medium">Código</th>
              <th className="py-2 pr-3 font-medium">Descrição</th>
              <th className="py-2 pr-3 text-right font-medium">Forecast</th>
              <th className="py-2 pr-3 text-right font-medium">Faturado</th>
              <th className="py-2 pr-3 text-right font-medium">% do forecast</th>
              {tabela.proximos.map((m) => (
                <th key={m.key} className="whitespace-nowrap py-2 pr-3 text-right font-medium">
                  Forecast {mesCurto(m.label)}
                </th>
              ))}
              <th className="whitespace-nowrap py-2 pr-3 font-medium">Principal UF</th>
              <th className="py-2 font-medium">
                <span className="sr-only">Situação</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {tabela.linhas.map((l) => (
              <Linha key={l.codigo} l={l} medida={medida} />
            ))}
          </tbody>
        </table>
      </div>
    )}
  </PainelCard>
);

export default SkuVendasTable;
